import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ElementRef
} from '@angular/core';
import { EcuNamesService } from "../../service/Ecu-names.service";
import { EcuBuildNameModel } from "../../models/EcuBuildName-model";
import { fadeInAnimation } from "../../animations/fadeInAnimation";
import { EcuProducersService } from "../../service/ecu-producers.service";

@Component({
    selector: 'app-ecu-build-names-table',
    templateUrl: './ecu-build-names-table.component.html',
    styleUrls: ['./ecu-build-names-table.component.css'],
    animations: [fadeInAnimation],
    host: { '[@fadeInAnimation]': '' }
})
export class EcuBuildNamesTableComponent implements OnInit, OnChanges {

    duplicateTuningName = false;
    duplicateValue = '';
    editingDraft: EcuBuildNameModel | null = null;
    private expandedNameFields = new Set<string>();

    @ViewChild('tableContainer', { static: false }) tableContainer!: ElementRef;

    public displayedColumns: string[] = ['id', 'correctEcuBuildNames', 'acceptedEcuBuildNames', 'actions'];

    @Input() ecuBuild: EcuBuildNameModel = {} as EcuBuildNameModel;
    @Input() searchQueryCorrectEcuNamesInput = '';
    @Output() correctEcuBuildName = new EventEmitter<string>();

    public ecuNamesService = inject(EcuNamesService);
    public ecuProducersService = inject(EcuProducersService);

    ngOnInit() {
        this.ecuNamesService.searchQuery.set('');
        this.getEcuProducersList();
        this.getEcuNames();

        if (this.searchQueryCorrectEcuNamesInput) {
            this.searchEcuNames(this.searchQueryCorrectEcuNamesInput);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['ecuBuild'] && this.hasMeaningfulEcuBuild(this.ecuBuild)) {
            this.ecuNamesService.ecuNames.update(ecuNames =>
                ecuNames.some(item => item.id === this.ecuBuild.id)
                    ? ecuNames
                    : [this.ecuBuild, ...ecuNames]
            );
        }
        if (changes['searchQueryCorrectEcuNamesInput']) {
            this.searchEcuNames(this.searchQueryCorrectEcuNamesInput ?? '');
        }
    }

    private hasMeaningfulEcuBuild(ecu: EcuBuildNameModel | undefined): boolean {
        return !!ecu && (ecu.id != null || !!ecu.correctEcuBuildNames);
    }

    getEcuNames() {
        this.ecuNamesService.getEcuBuildNames();
    }

    getEcuProducersList() {
        this.ecuProducersService.getAllCorrectEcuProducers();
    }

    toggleInputRow(): void {
        this.cancelEdit();
        this.ecuNamesService.showInputRow.set(!this.ecuNamesService.showInputRow());
        this.ecuNamesService.resetNewRowValue();
        this.resetDuplicateWarning();

        setTimeout(() => {
            if (this.tableContainer) {
                this.tableContainer.nativeElement.scrollTop = 0;
            }
        }, 0);
    }

    addNewRow(): void {
        this.ecuNamesService.addNewEcuBuildItem(this.ecuNamesService.newRow());
        this.resetDuplicateWarning();

        setTimeout(() => {
            if (this.tableContainer) {
                this.tableContainer.nativeElement.scrollTop = 0;
            }
        }, 0);
    }

    deleteRow() {
        this.ecuNamesService.showInputRow.set(false);
        this.resetDuplicateWarning();
    }

    deleteItemFromDatabase(ecu: EcuBuildNameModel) {
        this.ecuNamesService.deleteEcuBuildItem(ecu);
    }

    editEcu(ecu: EcuBuildNameModel) {
        if (this.ecuNamesService.isWaitingResponse()) return;
        this.ecuNamesService.showInputRow.set(false);
        this.editingDraft = {...ecu};
        this.ecuNamesService.ecuToEditIndex.set(this.ecuNamesService.ecuNamesFiltered().findIndex(item => item.id === ecu.id));
        this.resetDuplicateWarning();
    }

    cancelEdit() {
        this.editingDraft = null;
        this.ecuNamesService.closeEditedItem();
        this.resetDuplicateWarning();
    }

    isDuplicateOnEdit(value: string, currentId: number): boolean {
        if (!value || !value.trim()) return false;

        const lowerValue = value.toLowerCase().trim();

        return this.ecuNamesService.ecuNames()
            .some(e => e.correctEcuBuildNames?.toLowerCase().trim() === lowerValue && e.id !== currentId);
    }


    saveEditedItem() {
        if (!this.editingDraft || this.ecuNamesService.isWaitingResponse()) return;
        const draft = this.editingDraft;
        if (!draft.correctEcuBuildNames?.trim()) return;
        if (this.isDuplicateOnEdit(draft.correctEcuBuildNames, draft.id)) {
            this.duplicateTuningName = true;
            this.duplicateValue = draft.correctEcuBuildNames;
            return;
        }

        this.ecuNamesService.updateEcuBuild(draft, draft.id).subscribe({
            next: () => this.cancelEdit(),
            error: () => { /* Keep the draft open so it can be retried. */ }
        });
    }

    onNewTuningNameEdit(value: string, currentId: number): void {
        this.duplicateValue = value;
        this.duplicateTuningName = this.isDuplicateOnEdit(value, currentId);
    }




    searchForCorrectEcuNames(event: any) {
        this.searchEcuNames(event.target.value);
    }

    searchEcuNames(searchQuery: string) {
        this.cancelEdit();
        this.ecuNamesService.searchEcuNames(searchQuery);
    }

    private names(value: string | null | undefined): string[] {
        return (value ?? '').split(',').map(name => name.trim()).filter(Boolean);
    }

    private nameFieldKey(id: number, field: string): string {
        return `${id}:${field}`;
    }

    visibleNames(value: string | null | undefined, id: number, field: string): string[] {
        const names = this.names(value);
        return this.expandedNameFields.has(this.nameFieldKey(id, field)) ? names : names.slice(0, 3);
    }

    hiddenNamesCount(value: string | null | undefined, id: number, field: string): number {
        return this.expandedNameFields.has(this.nameFieldKey(id, field)) ? 0 : Math.max(0, this.names(value).length - 3);
    }

    canCollapseNames(value: string | null | undefined, id: number, field: string): boolean {
        return this.expandedNameFields.has(this.nameFieldKey(id, field)) && this.names(value).length > 3;
    }

    toggleNames(id: number, field: string): void {
        const key = this.nameFieldKey(id, field);
        if (this.expandedNameFields.has(key)) this.expandedNameFields.delete(key);
        else this.expandedNameFields.add(key);
    }

    getCorrectEcuBuildName(event: any) {
        const value = (event.target.textContent || '').trim();
        if (value) {
            this.correctEcuBuildName.emit(value);
        }
    }

    onRefreshTable(): void {
        this.cancelEdit();
        this.ecuNamesService.refreshEcuBuildTable();
        this.resetDuplicateWarning();

        setTimeout(() => {
            if (this.tableContainer) {
                this.tableContainer.nativeElement.scrollTop = 0;
            }
        }, 0);
    }

    onNewTuningNameInput(value: string): void {
        this.duplicateValue = value;

        if (!value || !value.trim()) {
            this.duplicateTuningName = false;
            return;
        }

        const isDuplicate = this.ecuNamesService.checkDuplicateEcuBuildName(value);
        this.duplicateTuningName = isDuplicate;
    }

    resetDuplicateWarning(): void {
        this.duplicateTuningName = false;
        this.duplicateValue = '';
    }

}
